import type { JobDetail } from '../../api/jobs'
import { matchesIdentifier } from '../../lib/identity'
import { isPastTranscriberStage } from '../../lib/jobStatus'

export type WorkspaceMode = 'transcribe' | 'review'

/** What the screen lets the user do with this job, and why. */
export interface WorkspaceAccess {
  readonly mode: WorkspaceMode
  readonly canEdit: boolean
  /** Set when editing is refused — shown to explain the read-only state. */
  readonly readOnlyReason?: string
  /** The job is `open` and this user should claim it before editing (04 §4c). */
  readonly needsClaim: boolean
}

// The editable set lives in lib/jobStatus so this screen and the 06 R3
// mutation guard cannot drift apart — two lists that disagree is exactly how a
// duplicate submit gets through.

/**
 * Is this job assigned to the person looking at it?
 *
 * **The session is known by more than one identifier**, and they do not look
 * alike. The token's `user_id` is a decimal string ("2") while a job's
 * `assignee_id` is a UUID; the token carries that UUID as well, but
 * *unhyphenated* ("de7ec7ed0000000000000000000000a1" vs
 * "de7ec7ed-0000-0000-0000-0000000000a1"). Comparing the wrong one — or the
 * right one without normalising — makes a transcriber's own freshly claimed job
 * read as someone else's, and the workspace locks the form they were sent there
 * to fill in. That is exactly what happened before this existed (verified
 * against staging 2026-07-29).
 *
 * So: match against EVERY identifier the session is known by, hyphens and case
 * ignored. Being permissive here is the safe direction — a false negative locks
 * someone out of their own work, while a false positive requires colliding
 * identifiers across two users, which these formats do not admit.
 */
export function isOwnedBy(assigneeId: string | null, identifiers: readonly string[]): boolean {
  // Delegates to lib/identity: the presence frame needs the same rule, and two
  // copies of it would drift apart exactly where it is expensive to be wrong.
  return matchesIdentifier(assigneeId, identifiers)
}

/**
 * Decide the workspace's mode and whether the form is editable —
 * docs/spec/04 §4c.
 *
 * Pure so every combination of status, role and ownership can be enumerated in
 * tests. The wrong answer here is expensive in both directions: letting two
 * transcribers edit one envelope produces conflicting submissions, while
 * locking a job someone legitimately owns stops their shift.
 *
 * `requestedMode` is the URL's `?mode=`. It is a hint, not an instruction — a
 * hand-edited URL must not grant an admin action to someone without the role,
 * or open an editor on a job that has moved past editing.
 */
export function resolveAccess(
  job: JobDetail,
  options: {
    readonly isAdmin: boolean
    /** Every identifier this session is known by — see `isOwnedBy`. */
    readonly currentUserIds: readonly string[]
    readonly requestedMode?: WorkspaceMode | undefined
  },
): WorkspaceAccess {
  const { isAdmin, currentUserIds, requestedMode } = options
  const status = job.status

  // QA review: an admin looking at a job waiting for QA.
  if (status === 'transcription_qa_check') {
    if (isAdmin) {
      return { mode: 'review', canEdit: true, needsClaim: false }
    }
    return {
      mode: 'review',
      canEdit: false,
      readOnlyReason: 'This job is waiting for an admin to review it.',
      needsClaim: false,
    }
  }

  // An admin can ask to review anything, but only to look — the review
  // endpoint only accepts a job in QA.
  if (requestedMode === 'review' && isAdmin) {
    return {
      mode: 'review',
      canEdit: false,
      readOnlyReason: 'This job is not waiting for review.',
      needsClaim: false,
    }
  }

  if (isPastTranscriberStage(status)) {
    return {
      mode: 'transcribe',
      canEdit: false,
      readOnlyReason: 'This job has already moved on and can no longer be edited.',
      needsClaim: false,
    }
  }

  // Someone else's in-progress work. Read-only, and say whose (04 §4c) — a
  // bare "read only" leaves the user thinking the app is broken.
  const assignee = job.assignee_id
  if (
    status === 'transcription_in_progress' &&
    assignee !== null &&
    currentUserIds.length > 0 &&
    !isOwnedBy(assignee, currentUserIds)
  ) {
    const who = job.assignee_name ?? 'another transcriber'
    return {
      mode: 'transcribe',
      canEdit: false,
      readOnlyReason: `This job is assigned to ${who}.`,
      needsClaim: false,
    }
  }

  return {
    mode: 'transcribe',
    canEdit: true,
    // Claim before editing so the job is owned by this user (04 §4c). An
    // in-progress job the caller already holds needs no second claim.
    needsClaim: status === 'open',
  }
}

/** Narrow an arbitrary `?mode=` value. */
export function parseMode(raw: unknown): WorkspaceMode | undefined {
  return raw === 'transcribe' || raw === 'review' ? raw : undefined
}
